import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAdminApi } from '../hooks/useAdminApi';
import { ArrowLeft, Printer, FileImage } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { roundToQuarterHour, formatHoursDecimal } from '../../lib/hoursFormat';

const PrintReportPage = () => {
  const [searchParams] = useSearchParams();
  const reportNumber = searchParams.get('report');
  const driverId = searchParams.get('driver');
  const { get } = useAdminApi();
  
  const [report, setReport] = useState(null);
  const [driver, setDriver] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({});
  const [loading, setLoading] = useState(true);

  // Force white background on body when this page is mounted
  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    const originalColor = document.body.style.color;
    const originalOverflow = document.body.style.overflow;
    document.body.style.backgroundColor = 'white';
    document.body.style.color = 'black';
    document.documentElement.style.backgroundColor = 'white';
    return () => {
      document.body.style.backgroundColor = originalBg;
      document.body.style.color = originalColor;
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!driverId || !reportNumber) return;
      
      const driversRes = await get('/saas/dashboard/drivers');
      if (driversRes.success) {
        const found = driversRes.data.find(d => d.id === driverId);
        setDriver(found);
      }
      
      const reportsRes = await get(`/saas/driver/${driverId}/reports`);
      if (reportsRes.success) {
        const foundReport = reportsRes.data.find(r => r.report_number === reportNumber);
        setReport(foundReport);
      }

      // Fetch company settings (same endpoint as driver app)
      const settingsRes = await get('/settings/company');
      if (settingsRes.success && settingsRes.data?.company_info) {
        setCompanyInfo(settingsRes.data.company_info);
      }
      
      setLoading(false);
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId, reportNumber]);

  const calculateHours = (report) => {
    let totalHours = 0;
    let pauseHours = 0;
    
    if (report?.start_time && report?.end_time) {
      const startDate = report.date;
      const endDate = report.end_date || report.date;
      const start = new Date(`${startDate}T${report.start_time}`);
      let end = new Date(`${endDate}T${report.end_time}`);
      // Rollover implicite si end <= start sans end_date
      if (end <= start) {
        end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
      }
      totalHours = Math.max(0, (end - start) / (1000 * 60 * 60));
    }
    
    report?.stops?.forEach(stop => {
      if ((stop.stop_type === 'pause' || stop.stop_type === 'fin_trajet') && stop.arrival_datetime && stop.departure_datetime) {
        const arrival = new Date(stop.arrival_datetime);
        const departure = new Date(stop.departure_datetime);
        pauseHours += (departure - arrival) / (1000 * 60 * 60);
      }
    });
    
    const payableRaw = Math.max(0, totalHours - pauseHours);
    return {
      totalHours: roundToQuarterHour(totalHours),
      pauseHours: roundToQuarterHour(pauseHours),
      payableHours: roundToQuarterHour(payableRaw),
    };
  };

  const getAllDocuments = () => {
    const docs = [];
    report?.stops?.forEach(stop => {
      // Only include scans from delivery stops in the printed report
      if (stop.stop_type === 'delivery') {
        stop.scanned_documents?.forEach(doc => {
          docs.push({ ...doc, stopName: stop.client_name });
        });
      }
    });
    return docs;
  };

  const getDocTypeLabel = (type) => {
    const types = { 'delivery_note': 'Bon de livraison', 'signature': 'Signature', 'photo': 'Photo', 'invoice': 'Facture' };
    return types[type] || type || 'Document';
  };

  const getStopTypeLabel = (type) => {
    const labels = { 'pickup': 'Cueillette', 'delivery': 'Livraison', 'pause': 'Pause', 'fin_trajet': 'Fin de trajet' };
    return labels[type] || type;
  };

  const getEquipmentLabel = (equip) => {
    const labels = { 'moffett': 'Moffett', 'transpalette': 'Transpalette', 'lift': 'Lift', 'none': '' };
    return labels[equip] || equip || '';
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6b7280' }}>Chargement...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'white', padding: '2rem' }}>
        <p style={{ color: '#ef4444' }}>Rapport non trouve</p>
        <Link to="/admin/drivers" style={{ color: '#3b82f6', textDecoration: 'underline', marginTop: '1rem', display: 'block' }}>Retour</Link>
      </div>
    );
  }

  const hours = calculateHours(report);
  const allDocs = getAllDocuments();

  return (
    <div id="print-report-page" style={{ 
      minHeight: '100vh', 
      backgroundColor: '#ffffff', 
      color: '#000000',
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      overflow: 'auto',
      zIndex: 9999
    }}>
      <style>{`
        #print-report-page { background-color: white !important; }
        #print-report-page .print-content, #print-report-page .print-content * { background-color: white !important; color: black !important; }
        #print-report-page .no-print { background-color: #0f172a !important; color: #cbd5e1 !important; }
        #print-report-page .no-print * { color: #cbd5e1 !important; }
        #print-report-page .no-print a { color: #cbd5e1 !important; }
        #print-report-page .no-print button { background-color: #f59e0b !important; color: #000000 !important; }
        #print-report-page .no-print button * { color: #000000 !important; }
        #print-report-page .stop-badge-pickup { background-color: #dbeafe !important; color: #1d4ed8 !important; }
        #print-report-page .stop-badge-pause { background-color: #ffedd5 !important; color: #c2410c !important; }
        #print-report-page .stop-badge-delivery { background-color: #dcfce7 !important; color: #15803d !important; }
        #print-report-page .stop-badge-fin_trajet { background-color: #f3e8ff !important; color: #7c3aed !important; }
        #print-report-page .hours-highlight { background-color: #fef3c7 !important; border: 2px solid #f59e0b !important; }
        #print-report-page .hours-highlight p { color: #b45309 !important; }
        #print-report-page .hours-highlight .value { color: #d97706 !important; }
        @media print {
          html, body { background: white !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          #print-report-page { position: static !important; overflow: visible !important; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always !important; }
          .document-full-page-container { page-break-before: always !important; page-break-after: always !important; page-break-inside: avoid !important; margin: 0 !important; padding: 0 !important; width: 100% !important; height: 100vh !important; }
          .document-print-area { width: 100% !important; height: 100% !important; margin: 0 !important; padding: 0 !important; display: flex !important; align-items: center !important; justify-content: center !important; border: none !important; }
          .scanned-document-image { width: 100% !important; height: 100% !important; max-width: none !important; max-height: none !important; object-fit: contain !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
      
      {/* Print Controls - Hidden when printing */}
      <div className="no-print" style={{ backgroundColor: '#0f172a', color: 'white', padding: '1rem', position: 'sticky', top: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to={`/admin/drivers/${driverId}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#cbd5e1', textDecoration: 'none' }}>
          <ArrowLeft size={20} />
          Retour au chauffeur
        </Link>
        <Button onClick={handlePrint} style={{ backgroundColor: '#f59e0b', color: 'black', fontWeight: 'bold' }}>
          <Printer size={16} style={{ marginRight: '0.5rem' }} />
          Imprimer
        </Button>
      </div>

      {/* Printable Content */}
      <div className="print-content" style={{ maxWidth: '56rem', margin: '0 auto', padding: '2rem', backgroundColor: 'white', color: 'black' }}>
        
        {/* Company Header with Logo */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', paddingBottom: '1rem', marginBottom: '1rem', borderBottom: '3px solid #f59e0b' }}>
          {companyInfo.logo_base64 && (
            <img 
              src={companyInfo.logo_base64} 
              alt="Logo" 
              style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
          )}
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.25rem' }}>{companyInfo.name || ''}</h1>
            {companyInfo.address && <p style={{ color: '#4b5563', fontSize: '0.875rem', whiteSpace: 'pre-line' }}>{companyInfo.address}</p>}
            <p style={{ color: '#4b5563', fontSize: '0.875rem' }}>
              {companyInfo.phone}{companyInfo.email && ` \u2022 ${companyInfo.email}`}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#d97706' }}>RAPPORT JOURNALIER</h2>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>{report.report_number}</p>
            <span style={{
              display: 'inline-block', marginTop: '0.25rem', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '500',
              backgroundColor: report.status === 'completed' ? '#dcfce7' : '#fef3c7',
              color: report.status === 'completed' ? '#166534' : '#92400e'
            }}>
              {report.status === 'completed' ? 'TERMINE' : 'BROUILLON'}
            </span>
          </div>
        </div>

        {/* Sous-traitant */}
        {report.subcontractor && (
          <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
            <span style={{ color: '#0369a1', fontWeight: '600', fontSize: '0.875rem' }}>Sous-traitant: </span>
            <span style={{ color: '#0c4a6e', fontSize: '0.875rem' }}>{report.subcontractor}</span>
          </div>
        )}

        {/* Driver, Date & Vehicle Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ backgroundColor: '#f3f4f6', padding: '0.75rem', borderRadius: '0.5rem' }}>
            <p style={{ color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '500', marginBottom: '0.25rem' }}>Chauffeur</p>
            <p style={{ fontSize: '1rem', fontWeight: 'bold', color: '#111827' }}>{driver?.first_name} {driver?.last_name}</p>
          </div>
          <div style={{ backgroundColor: '#f3f4f6', padding: '0.75rem', borderRadius: '0.5rem' }}>
            <p style={{ color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '500', marginBottom: '0.25rem' }}>Date</p>
            <p style={{ fontSize: '1rem', fontWeight: 'bold', color: '#111827' }}>
              {report.date}
              {report.end_date && report.end_date !== report.date && ` au ${report.end_date}`}
            </p>
          </div>
          <div style={{ backgroundColor: '#f3f4f6', padding: '0.75rem', borderRadius: '0.5rem' }}>
            <p style={{ color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '500', marginBottom: '0.25rem' }}>Vehicule</p>
            <p style={{ fontSize: '1rem', fontWeight: 'bold', color: '#111827' }}>
              {report.vehicle_label || report.vehicle_name || report.vehicle_plate || '--'}
            </p>
          </div>
        </div>

        {/* Hours Summary - 4 columns like driver app */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div style={{ backgroundColor: '#f3f4f6', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Debut</p>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>{report.start_time || '--:--'}</p>
          </div>
          <div style={{ backgroundColor: '#f3f4f6', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Fin</p>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>{report.end_time || '--:--'}</p>
          </div>
          <div className="hours-highlight" style={{ backgroundColor: '#fef3c7', border: '2px solid #f59e0b', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center' }}>
            <p style={{ color: '#b45309', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '500', marginBottom: '0.25rem' }}>Heures payables</p>
            <p className="value" style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#d97706' }}>{formatHoursDecimal(hours.payableHours)}</p>
          </div>
          <div style={{ backgroundColor: '#f3f4f6', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Distance</p>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>{report.total_km || '--'} km</p>
          </div>
        </div>

        {/* Stops */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 'bold', borderBottom: '2px solid #d1d5db', paddingBottom: '0.5rem', marginBottom: '1rem', color: '#111827' }}>
            Arrets ({report.stops?.length || 0})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {report.stops?.sort((a, b) => (a.order || 0) - (b.order || 0)).map((stop, index) => {
              const bgColor = stop.stop_type === 'pickup' ? '#dbeafe' : stop.stop_type === 'pause' ? '#ffedd5' : stop.stop_type === 'fin_trajet' ? '#f3e8ff' : '#dcfce7';
              const textColor = stop.stop_type === 'pickup' ? '#1d4ed8' : stop.stop_type === 'pause' ? '#c2410c' : stop.stop_type === 'fin_trajet' ? '#7c3aed' : '#15803d';
              const numBg = stop.stop_type === 'pickup' ? '#3b82f6' : stop.stop_type === 'pause' ? '#f97316' : stop.stop_type === 'fin_trajet' ? '#8b5cf6' : '#22c55e';
              
              return (
                <div key={stop.id || index} style={{ border: '1px solid #d1d5db', borderRadius: '0.5rem', padding: '0.75rem', pageBreakInside: 'avoid', backgroundColor: 'white' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <div style={{
                      width: '1.75rem', height: '1.75rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 'bold', fontSize: '0.75rem', flexShrink: 0, backgroundColor: numBg
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: '1rem', color: '#111827' }}>{stop.client_name}</strong>
                        <span style={{
                          fontSize: '0.7rem', padding: '0.2rem 0.5rem', borderRadius: '0.25rem',
                          backgroundColor: bgColor, color: textColor
                        }}>
                          {getStopTypeLabel(stop.stop_type)}
                        </span>
                      </div>
                      
                      {stop.address && <p style={{ color: '#4b5563', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Adresse: {stop.address}</p>}
                      
                      {stop.stop_type === 'pause' && stop.pause_type && (
                        <p style={{ color: '#ea580c', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                          {stop.pause_type === 'lunch' ? 'Diner' : stop.pause_type === 'dinner' ? 'Souper' : stop.pause_type === 'break' ? 'Pause cafe' : 'Nuit / Repos'}
                        </p>
                      )}
                      
                      {/* Quantities */}
                      {stop.quantities && (stop.quantities.palettes > 0 || stop.quantities.units > 0 || stop.quantities.packages > 0) && (
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                          {stop.quantities.palettes > 0 && (
                            <span style={{ backgroundColor: '#e5e7eb', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem', color: '#374151' }}>{stop.quantities.palettes} palettes</span>
                          )}
                          {stop.quantities.units > 0 && (
                            <span style={{ backgroundColor: '#e5e7eb', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem', color: '#374151' }}>{stop.quantities.units} unites</span>
                          )}
                          {stop.quantities.packages > 0 && (
                            <span style={{ backgroundColor: '#e5e7eb', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem', color: '#374151' }}>{stop.quantities.packages} paquets</span>
                          )}
                        </div>
                      )}

                      {/* Equipment */}
                      {stop.equipment_used && stop.equipment_used !== 'none' && (
                        <p style={{ color: '#7c3aed', fontSize: '0.8rem', marginTop: '0.25rem', fontWeight: '500' }}>
                          Equipement: {getEquipmentLabel(stop.equipment_used)}
                        </p>
                      )}
                      
                      {/* Delivery note numbers */}
                      {stop.delivery_note_numbers?.length > 0 && (
                        <p style={{ color: '#4b5563', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                          <strong>Bons:</strong> {stop.delivery_note_numbers.join(', ')}
                        </p>
                      )}
                      
                      {/* Times */}
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.7rem', color: '#6b7280', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                        {stop.arrival_datetime && <span>Arrivee: {new Date(stop.arrival_datetime).toLocaleString('fr-CA')}</span>}
                        {stop.departure_datetime && <span>Depart: {new Date(stop.departure_datetime).toLocaleString('fr-CA')}</span>}
                      </div>
                      
                      {/* Scanned documents - only show in print for delivery stops */}
                      {stop.scanned_documents?.length > 0 && (
                        <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #e5e7eb' }} className={stop.stop_type !== 'delivery' ? 'no-print' : ''}>
                          <p style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <FileImage size={12} />
                            Documents scannes ({stop.scanned_documents.length}):
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                            {stop.scanned_documents.map((doc, idx) => (
                              <span key={idx} style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.65rem' }}>
                                {doc.filename || `Document ${idx + 1}`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Documents Summary */}
        {allDocs.length > 0 && (
          <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '0.5rem', padding: '1rem', marginBottom: '2rem' }}>
            <h3 style={{ fontWeight: 'bold', fontSize: '1rem', color: '#92400e', marginBottom: '0.25rem' }}>Documents scannes: {allDocs.length}</h3>
            <p style={{ color: '#a16207', fontSize: '0.8rem' }}>Les documents sont inclus dans les pages suivantes.</p>
          </div>
        )}

        {/* Footer */}
        <div style={{ borderTop: '2px solid #e5e7eb', paddingTop: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.8rem' }}>
          <p>Rapport genere le {new Date().toLocaleString('fr-CA')}</p>
        </div>

        {/* Document Pages - Full Page for Print */}
        {allDocs.map((doc, index) => (
          <div 
            key={doc.id || index} 
            className="print-break document-full-page-container" 
            style={{ marginTop: '2rem', pageBreakBefore: 'always', pageBreakAfter: 'always', pageBreakInside: 'avoid' }}
          >
            <div className="no-print" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ fontWeight: 'bold', fontSize: '1rem', color: '#111827', marginBottom: '0.25rem' }}>Document {index + 1}/{allDocs.length}</h3>
              <p style={{ color: '#4b5563', fontSize: '0.8rem' }}>
                <strong>{doc.filename || 'Document'}</strong> - {getDocTypeLabel(doc.type)} - Arret: {doc.stopName}
              </p>
            </div>
            <div 
              className="document-print-area"
              style={{ width: '100%', minHeight: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'white' }}
            >
              {(() => {
                const url = doc.data || doc.image_url || '';
                const imgSrc = url.startsWith('data:') ? url : (url ? `${process.env.REACT_APP_BACKEND_URL}${url}` : '');
                return imgSrc ? (
                  <img 
                    src={imgSrc} 
                    alt={doc.filename || 'Document'} 
                    className="scanned-document-image"
                    style={{ width: '100%', height: 'auto', maxHeight: '100vh', objectFit: 'contain' }} 
                  />
                ) : (
                  <p style={{ color: '#9ca3af' }}>Apercu non disponible</p>
                );
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrintReportPage;
